/**
 * ArtifactViewPage — the member-gated viewer for one published artifact
 * (/artifact/:id). Cloud port of the self-host dashboard's #/artifact/<id>
 * view (backend/selfhosted/ui/index.html — artifactViewPage).
 *
 * The artifact skill publishes self-contained shareable pages (reports, plans,
 * write-ups); the ONLY read path is the authed, member-gated
 * GET /credits/artifacts/:id → { metadata, content, format, rendered }
 * (src/handlers/artifacts.js — there is deliberately NO unauthenticated serve
 * route). Login is enforced by ProtectedRoute; project MEMBERSHIP is enforced
 * server-side, so a non-member sees the error state, never the content.
 *
 * `rendered` is the full HTML document (markdown already converted, CSP <meta>
 * blocking all network egress injected). We drop it into a SANDBOXED iframe via
 * srcDoc with sandbox="allow-scripts" ONLY — the opaque origin means the
 * untrusted artifact JS can't read the session token, and no allow-same-origin
 * / allow-top-navigation is ever added.
 *
 * TODO(artifacts-tab): the agent-detail "Artifacts" LIST (self-host reads the
 * kv-memory index via POST /credits/review-memory op:recall-prefix,
 * scopePrefix "<workflowType>:artifact:") is not ported yet — artifacts.js has
 * no list export, so cloud v1 ships the viewer page only; agents hand out the
 * direct URL.
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Navigate, Link } from 'react-router-dom';
import { IonSpinner } from '@ionic/react';
import { accountApi } from '../../services/api';
import './ArtifactViewPage.css';

// Same shape the backend enforces (ARTIFACT_ID_RE) — reject junk before
// spending the round-trip.
const ARTIFACT_ID_RE = /^[a-f0-9-]{36}$/;

const EMPTY_DOC = '<!doctype html><meta charset="utf-8"><p>Empty artifact.</p>';

const ArtifactViewPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ARTIFACT_ID_RE.test(id || '')) {
      setLoading(false);
      setError('Invalid artifact link.');
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    // /credits/* rides the account API client (same base the credit ledger
    // uses — the CreditsApi RestApi is mapped under the main custom domain
    // with the '/credits' basePath stripped before the handler).
    accountApi
      .get(`/credits/artifacts/${encodeURIComponent(id)}`)
      .then((res) => { if (!cancelled) setData(res || null); })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'You may not have access to this artifact.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const meta = data?.metadata || null;
  const title = meta?.title || 'Artifact';

  let body;
  if (loading) {
    body = (
      <div className="artifact-view-loading" role="status">
        <IonSpinner name="dots" />
        <span>Loading artifact…</span>
      </div>
    );
  } else if (error) {
    body = (
      <div className="artifact-view-error" role="alert">
        <h2>Can’t view this artifact</h2>
        <p>{error}</p>
        <Link className="artifact-view-back" to="/projects">Back to projects</Link>
      </div>
    );
  } else {
    body = (
      <iframe
        className="artifact-view-frame"
        title={title}
        // Opaque origin: no credentials, no top-nav. The rendered doc also
        // carries a CSP <meta> blocking all network egress (backend-injected).
        sandbox="allow-scripts"
        srcDoc={data?.rendered || EMPTY_DOC}
      />
    );
  }

  return (
    <div className="artifact-view-page">
      <Helmet>
        <title>{title} — Zibby</title>
      </Helmet>
      <header className="artifact-view-head">
        <div className="artifact-view-title-row">
          {meta?.favicon && (
            <span className="artifact-view-favicon" aria-hidden="true">{meta.favicon}</span>
          )}
          <h1 className="artifact-view-title">{title}</h1>
        </div>
        <div className="artifact-view-meta">
          <span>Shared page · sandboxed viewer (no network, no credentials)</span>
          {meta?.kind && <span className="artifact-view-meta-sep">·</span>}
          {meta?.kind && <span>{meta.kind}</span>}
          {meta?.updatedAt && <span className="artifact-view-meta-sep">·</span>}
          {meta?.updatedAt && (
            <span>Updated {new Date(meta.updatedAt).toLocaleString()}</span>
          )}
        </div>
      </header>
      {body}
    </div>
  );
};

/**
 * Compat shim for the backend's minted share URLs. The write path advertises
 * `<ZIBBY_PUBLIC_URL>/ui#/artifact/<id>` (the self-host dashboard route shape);
 * on cloud there is no /ui and BrowserRouter ignores the hash, so a bare visit
 * would bounce to /. Registered at /ui, this translates the hash form onto the
 * real /artifact/:id route (and falls through to / for any other /ui visit).
 */
export const UiArtifactRedirect = () => {
  const m = (window.location.hash || '').match(/^#\/artifact\/([a-f0-9-]{36})$/);
  return <Navigate to={m ? `/artifact/${m[1]}` : '/'} replace />;
};

export default ArtifactViewPage;
